/**
 * The one place the frontend names an ATS provider.
 *
 * There used to be two copies of this map — one in JobSearch.jsx for the
 * filter dropdowns, one in IndexHorizon.jsx for the index panel — plus the
 * scannable list. Adding a provider meant editing three places, and when ADP,
 * Recruiterbox, softgarden and Gem were added, both label maps were missed.
 * They kept working, because an unmapped provider silently falls back to its
 * own slug: ADP appeared in the source list as "adp", lowercase, among a dozen
 * properly capitalised names, and read as absent.
 *
 * `PROVIDER_LABELS` must cover every entry in the backend's
 * job_providers.PROVIDERS. A test asserts that, so the next provider cannot be
 * added without a name.
 */
export const PROVIDER_LABELS = {
  greenhouse: "Greenhouse",
  ashby: "Ashby",
  lever: "Lever",
  workable: "Workable",
  smartrecruiters: "SmartRecruiters",
  recruitee: "Recruitee",
  personio: "Personio",
  breezy: "Breezy",
  workday: "Workday",
  rippling: "Rippling",
  pinpoint: "Pinpoint",
  teamtailor: "Teamtailor",
  oracle: "Oracle",
  successfactors: "SAP SuccessFactors",
  recruiterbox: "Recruiterbox",
  softgarden: "Softgarden",
  gem: "Gem",
  adp: "ADP Workforce Now",
  usajobs: "USAJOBS",
};

/** Falls back to the slug rather than rendering nothing, but see above: a
 *  fallback that looks plausible is how four providers went unnamed. */
export const providerLabel = (slug) => PROVIDER_LABELS[slug] || slug;

/**
 * Providers that host a scannable company board. "web" is deliberately absent:
 * it is keyword search across aggregators. "usajobs" is absent too: it is one
 * board for the whole federal government, seeded by the product, not a
 * company anyone needs to ask for (the backend lists it in SYSTEM_PROVIDERS).
 * it is keyword search across aggregators, not a company slug you can scan, and
 * offering it here produced a confusing "Unknown provider" 400 from /scan.
 *
 * Ordered so the ones whose slug is not a company name come last — Workday is
 * tenant:wdN:site, Oracle is host:siteNumber, SuccessFactors is a bare
 * hostname. They are the least guessable and lean hardest on the slug hints.
 */
export const SCANNABLE_PROVIDERS = [
  "greenhouse", "ashby", "lever", "workable",
  "smartrecruiters", "recruitee", "personio", "breezy",
  "rippling", "pinpoint", "teamtailor",
  "recruiterbox", "softgarden", "gem", "adp",
  "workday", "oracle", "successfactors",
];
